import { useMemo, useState } from "react";

import { Modal, Select } from "components";
import { Button } from "expano-components";
import useGet from "hooks/useGet";
import useUpdate from "hooks/useUpdate";
import Loader from "components/Loader";
import { getKindLabel } from "utils/select_options/product_kind_options";

const ChangeProductGroupModal = ({
  product_id,
  product_kind,
  refetch,
  handleClose,
}) => {
  const [new_product_group_id, setNewProductGroupId] = useState(null);

  // Produkt mozna przepiac wylacznie do produktu grupowego tego samego rodzaju
  // (towar -> towar, usluga -> usluga) - filtrujemy liste po stronie API.
  const { is_loading: is_loading_product_groups, data: product_groups } =
    useGet({
      query: ["product_groups", "kind", product_kind],
      path: "/product_groups",
      context: "list",
      search: {
        per_page: 10_000,
        ...(product_kind ? { "q[kind_eq]": product_kind } : {}),
      },
    });

  const { is_fetching: is_validating, data: validation_data } = useGet({
    query: ["change_product_product_group_valid", new_product_group_id],
    path: `/products/${product_id}/validate_product_group_change`,
    search: { new_product_group_id },
    options: {
      enabled: !!new_product_group_id,
    },
  });

  const { is_updating, update: changeProductGroup } = useUpdate({
    path: `/products/${product_id}/change_product_group`,
    onSuccess: () => [refetch(), handleClose()],
  });

  const product_group_options = useMemo(() => {
    return product_groups?.map(({ id, name, sku }) => ({
      label: `[${sku}] ${name}`,
      value: id,
    }));
  }, [product_groups]);

  return (
    <Modal
      handleClose={handleClose}
      size="md"
      header={{
        title: "Zmień produkt grupowy",
      }}
    >
      {is_validating || is_loading_product_groups ? (
        <div className="row jc-c">
          <Loader />
        </div>
      ) : !new_product_group_id ? (
        <>
          {product_kind && (
            <p className="text-muted m-b-10">
              Dostępne są tylko produkty grupowe rodzaju:{" "}
              <strong>{getKindLabel(product_kind)}</strong>
            </p>
          )}
          <Select
            options={product_group_options}
            handleChange={(id) => setNewProductGroupId(id)}
          />
        </>
      ) : (
        <div className="text-center">
          {validation_data?.current_product_group?.id && (
            <>
              <p className="text-muted">
                {validation_data.current_product_group.sku}
              </p>
              <p>{validation_data.current_product_group.name}</p>
            </>
          )}
          <svg
            className="m-t-10 m-b-10"
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="30"
            viewBox="0 0 16 16"
          >
            <path
              fill-rule="evenodd"
              d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1"
            />
          </svg>
          {validation_data?.new_product_group?.id && (
            <>
              <p className="text-muted">
                {validation_data.new_product_group.sku}
              </p>
              <p>{validation_data.new_product_group.name}</p>
            </>
          )}
          <div className="m-t-20">
            {validation_data.is_valid ? (
              <>
                <p className="text-success m-b-10">
                  Produkt grupowy jest możliwy do zaktualizowania.
                </p>
                <p className="m-b-5 text-danger">
                  Produkt zostanie usunięty z kanałów:
                </p>
                {validation_data?.channels_to_remove?.map(({ id, name }) => (
                  <p className="text-muted" key={id}>
                    {name}
                  </p>
                ))}
                {validation_data?.channels_to_add?.length > 0 && (
                  <div className="m-t-20">
                    <p className="m-b-5 text-success">
                      Produkt zostanie dodany do kanałów:
                    </p>
                    {validation_data?.channels_to_add?.map(({ id, name }) => (
                      <p className="text-muted" key={id}>
                        {name}
                      </p>
                    ))}
                  </div>
                )}

                <div className="row jc-c m-t-20" style={{ gap: 10 }}>
                  <Button
                    text="Anuluj"
                    onClick={() => setNewProductGroupId(null)}
                  />
                  <Button
                    type="save"
                    text="Zmień"
                    disabled={is_updating}
                    onClick={() =>
                      changeProductGroup({
                        product_group_id: new_product_group_id,
                      })
                    }
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-danger">
                  Brak możliwości zaktualizowania produktu grupowego!
                </p>
                {validation_data?.errors?.map((error, index) => (
                  <p className="text-danger m-b-5" key={index}>
                    {error}
                  </p>
                ))}
                {validation_data?.active_offers?.length > 0 && (
                  <p>Produkt posiada aktywne oferty:</p>
                )}
                {validation_data?.active_offers?.map((offer) => (
                  <div key={offer.id} className="m-b-5">
                    <p className="text-muted">{offer.channel_name}</p>
                    <a className="text-link" href={offer.url}>
                      {offer.url}
                    </a>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ChangeProductGroupModal;
